import { useRoutes, Navigate } from 'react-router-dom';
// layouts
import HomeLayout from '../layouts/home'; // Keep HomeLayout as direct import - needed immediately
// Guard
import AuthGuard from '../auth/AuthGuard';
import GuestGuard from '../auth/GuestGuard';
import SetPasswordGuard from '../auth/SetPasswordGuard';
import OnboardingGuard from '../auth/OnboardingGuard';

// Import lazy components only when needed in routes
// This ensures proper code splitting
import * as LazyComponents from './elements';

// ----------------------------------------------------------------------

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <LazyComponents.LoginPage />
            </GuestGuard>
          ),
        },
        {
          path: 'setPassword/:token',
          element: (
            <SetPasswordGuard>
              <LazyComponents.SetPasswordPage />
            </SetPasswordGuard>
          ),
        },
        {
          path: 'forgotPassword',
          element: <LazyComponents.ForgotPasswordPage />,
        },
        {
          path: 'new-password',
          element: <LazyComponents.NewPasswordPage />,
        },
        {
          path: 'verify',
          element: <LazyComponents.VerifyCodePage />,
        },
        {
          path: 'resetPasswordVerify',
          element: <LazyComponents.ResetPasswordVerifyPage />,
        },
        {
          element: <LazyComponents.CompactLayout />,
          children: [
            { path: 'verifyPhone', element: <LazyComponents.VerifyPhonePage /> },
            { path: 'checkEmail', element: <LazyComponents.CheckEmailPage /> },
          ],
        },
        {
          path: 'downloadApp',
          element: <LazyComponents.DownloadAppAfterSetupPage />,
        },
        {
          path: 'reactivate',
          element: <LazyComponents.ReactivateAccountPage />,
        },
      ],
    },
    // Onboarding
    {
      path: 'onboarding',
      element: (
        <OnboardingGuard>
          <LazyComponents.OnboardingLayout />
        </OnboardingGuard>
      ),
      children: [
        { path: 'welcome', element: <Navigate to="/dashboard/getting-started" replace /> },
        { path: 'fence', element: <LazyComponents.FenceListPage /> },
        { path: 'fence/:id', element: <LazyComponents.FenceDetailPage /> },
        { path: 'download', element: <LazyComponents.DownloadAppPage /> },
      ],
    },
    { path: '/quote/:shareToken', element: <LazyComponents.PublicQuotePage /> },
    {
      path: '/quote/:shareToken/agreements/service',
      element: <LazyComponents.PublicQuoteAgreementFlowPage />,
    },
    {
      path: '/quote/:shareToken/agreements/sign',
      element: <LazyComponents.PublicQuoteAgreementFlowPage />,
    },
    {
      path: '/material-list/:shareToken',
      element: <LazyComponents.PublicMaterialListPage />,
    },
    {
      path: '/virtual-estimator/:companySlug',
      element: <LazyComponents.PublicVirtualEstimatorPage />,
    },
    {
      path: '/virtual-estimator/:companySlug/estimate/:publicAccessToken',
      element: <LazyComponents.PublicVirtualEstimatorPage />,
    },
    // Start — full-screen signup questionnaire (no header/footer)
    { path: '/start', element: <LazyComponents.StartPage /> },
    // Home
    {
      element: <HomeLayout />,
      children: [
        // { element: <HomePage />, index: true },
        { element: <LazyComponents.HomePage />, index: true },
        { path: '/subscription', element: <LazyComponents.SubscriptionPage /> },
        { path: '/blog/:id', element: <LazyComponents.BlogDetailPage /> },
        { path: '/blog', element: <LazyComponents.BlogsPage /> },
        { path: '/checkout', element: <LazyComponents.CheckoutPage /> },
        { path: '/contactus', element: <LazyComponents.ContactUsPage /> },
        { path: '/download', element: <LazyComponents.DownloadPage /> },
        { path: '/edfence', element: <LazyComponents.EDFencePage /> },
        { path: '/faqs', element: <LazyComponents.FaqsPage /> },
        { path: '/ourfences', element: <LazyComponents.OurFencesPage /> },
        { path: '/ourfences/:id', element: <LazyComponents.OurFenceDetailPage /> },
        { path: '/getStarted', element: <LazyComponents.SignUpPage /> },
        { path: '/signup', element: <Navigate to="/getStarted" replace /> },
        { path: '/sign-up', element: <Navigate to="/getStarted" replace /> },
        { path: '/register', element: <Navigate to="/getStarted" replace /> },
        { path: '/getstarted', element: <Navigate to="/getStarted" replace /> },
        { path: '/get-started', element: <Navigate to="/getStarted" replace /> },
        { path: '/aboutus', element: <LazyComponents.OurStoryPage /> },
        { path: '/features', element: <LazyComponents.FeaturesPage /> },
        { path: '/marketplace', element: <LazyComponents.MarketplacePage /> },
        { path: '/marketplace/apply', element: <LazyComponents.MarketplaceApplyPage /> },
        { path: '/quotes-and-materials', element: <LazyComponents.QuotesAndMaterialsPage /> },
        { path: '/ar', element: <LazyComponents.RealityARPage /> },
        { path: '/legal', element: <LazyComponents.LegalPage /> },
        { path: '/privacy-policy', element: <LazyComponents.PrivacyPolicyPage /> },
        { path: '/terms-and-conditions', element: <LazyComponents.TermsAndConditionsPage /> },
        // Meetings are no longer booked online; old links land on the contact page.
        { path: '/meeting', element: <Navigate to="/contactus" replace /> },
        { path: '/shopper', element: <LazyComponents.ShopperPage /> },
      ],
    },
    // Dashboard
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <LazyComponents.DashboardLayout />
        </AuthGuard>
      ),
      children: [
        // account
        { path: 'account', element: <LazyComponents.AccountPage /> },
        { path: 'account/choosenewplan', element: <LazyComponents.ChooseNewPlanPage /> },
        { path: 'account/almostdone', element: <LazyComponents.AlmostDonePage /> },
        { path: 'account/switchbilling', element: <LazyComponents.SwitchBillingPage /> },
        {
          path: 'account/updatePaymentMethod',
          element: <LazyComponents.UpdatePaymentMethodPage />,
        },
        { path: 'account/updateaccount', element: <LazyComponents.UpdateAccountPage /> },
        { path: 'account/updatechildusers', element: <LazyComponents.UpdateChildUsersPage /> },
        {
          path: 'account/confirmcancelsubscription',
          element: <LazyComponents.ConfirmCancelSubscriptionPage />,
        },
        // billing
        { path: 'billing', element: <LazyComponents.BillingHistoryPage /> },
        { path: 'billing/:id', element: <LazyComponents.BillingDetailPage /> },
        { path: 'blog', element: <LazyComponents.UserBlogsPage /> },
        { path: 'blog/:id', element: <LazyComponents.UserBlogDetailPage /> },
        { path: 'support', element: <LazyComponents.UserSupportPage /> },
        { path: 'contactus', element: <LazyComponents.UserContactPage /> },
        { path: 'download', element: <LazyComponents.UserDownloadPage /> },
        { path: 'recommended', element: <LazyComponents.RecommendedDevicePage /> },
        { path: 'getting-started', element: <LazyComponents.GettingStartedPage /> },
        { path: 'virtual-estimator', element: <LazyComponents.VirtualEstimatorPage /> },
        { path: 'virtual-estimator/leads', element: <LazyComponents.VirtualEstimatorLeadsPage /> },
        {
          path: 'virtual-estimator/leads/:leadId',
          element: <LazyComponents.VirtualEstimatorLeadsPage />,
        },
        { path: 'pricing-bom', element: <LazyComponents.PricingBomPage /> },
        {
          element: <LazyComponents.PricingBomAccessGuard />,
          children: [
            {
              path: 'pricing-bom/projects',
              element: <LazyComponents.PricingBomProjectsPage />,
            },
            {
              path: 'pricing-bom/customers',
              element: <LazyComponents.PricingBomProjectsPage />,
            },
            {
              path: 'pricing-bom/quotes',
              element: <LazyComponents.PricingBomQuotesPage />,
            },
            {
              path: 'pricing-bom/materials',
              element: <LazyComponents.PricingBomMaterialsPage />,
            },
            {
              path: 'pricing-bom/quotes/:id',
              element: <LazyComponents.PricingBomEstimateDetailPage />,
            },
            {
              path: 'pricing-bom/fence/:fenceId',
              element: <LazyComponents.PricingBomFenceDetailPage />,
            },
            {
              path: 'pricing-bom/branding',
              element: <LazyComponents.PricingBomBrandingPage />,
            },
          ],
        },
        // congratulation pages
        { path: 'account/planupdate/success', element: <LazyComponents.PlanUpdateSuccessPage /> },
        {
          path: 'account/switchbilling/success',
          element: <LazyComponents.SwitchBillingSuccessPage />,
        },
        {
          path: 'account/updatepaymentmethod/success',
          element: <LazyComponents.UpdatePaymentMethodSuccessPage />,
        },
        {
          path: 'account/pausesubscription/success',
          element: <LazyComponents.PauseSubscriptionSuccessPage />,
        },
        {
          path: 'account/cancelsubscription/success',
          element: <LazyComponents.CancelSubscriptionSuccessPage />,
        },
        {
          path: 'account/resumesubscription/success',
          element: <LazyComponents.ResumeSubscriptionSuccessPage />,
        },
      ],
    },
    // fence
    {
      element: (
        <AuthGuard>
          <OnboardingGuard>
            <LazyComponents.FenceTourWrapper>
              <LazyComponents.ManageFenceLayout />
            </LazyComponents.FenceTourWrapper>
          </OnboardingGuard>
        </AuthGuard>
      ),
      children: [
        {
          path: '/fence',
          element: <Navigate to="/fence/featured" replace />,
        },
        {
          path: '/fence/featured',
          element: <LazyComponents.FenceFeaturedPage />,
        },
        {
          path: '/fence/add',
          element: <LazyComponents.FenceAddPage />,
        },
        { path: '/fence/remove', element: <LazyComponents.FenceRemovePage /> },
        { path: '/fence/request', element: <LazyComponents.FenceRequestPage /> },
        {
          path: '/fence/view/:id',
          element: <LazyComponents.FenceDetailViewPage />,
        },
      ],
    },
    // Error
    {
      element: <LazyComponents.CompactLayout />,
      children: [
        { path: 'maintenance', element: <LazyComponents.MaintenancePage /> },
        { path: '500', element: <LazyComponents.Page500 /> },
        { path: '404', element: <LazyComponents.Page404 /> },
        { path: '403', element: <LazyComponents.Page403 /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}
